/**
 * Measure property getters and registration - NON-HOOK VERSION FOR POTREE
 *
 * This file contains only framework-agnostic code (no React hooks, no Apollo).
 * It can be safely imported by potree_fork without pulling in React dependencies.
 *
 * For React hook versions, see MeasureProperties.ts
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

/**
 * Register measure properties in the registry
 * Call this once at app initialization
 */
export function registerMeasureProperties() {
  // --- Label visibility properties ---

  if (!propertyRegistry.isRegistered('annotation_show_coordinates')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'annotation_show_coordinates',
      name: 'coordinate labels',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.POINT],
      ui: { group: 'appearance', order: 1 },
    });
  }

  if (!propertyRegistry.isRegistered('annotation_show_lengths')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'annotation_show_lengths',
      name: 'distance labels',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.DISTANCE, AnnotationType.AREA, AnnotationType.ROOM, AnnotationType.OPENING, AnnotationType.STRUCTURE],
      ui: { group: 'appearance', order: 1 },
    });
  }

  if (!propertyRegistry.isRegistered('annotation_show_area')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'annotation_show_area',
      name: 'area labels',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.AREA, AnnotationType.ROOM, AnnotationType.OPENING],
      ui: { group: 'appearance', order: 2 },
    });
  }

  if (!propertyRegistry.isRegistered('annotation_show_height')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'annotation_show_height',
      name: 'height labels',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.OPENING, AnnotationType.STRUCTURE],
      ui: { group: 'appearance', order: 4 },
    });
  }

  if (!propertyRegistry.isRegistered('annotation_show_volume')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'annotation_show_volume',
      name: 'volume labels',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.AREA, AnnotationType.ROOM],
      ui: { group: 'appearance', order: 3 },
    });
  }
}

// Register properties immediately when module is imported
registerMeasureProperties();

// ============================================================================
// NON-HOOK HELPERS FOR POTREE (uses global property service)
// ============================================================================

/** Get reactive property for showing coordinate labels (Point annotations) */
export function getAnnotationShowCoordinatesProperty(annotationId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_show_coordinates', annotationId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

/** Get reactive property for showing length labels (Distance + Area annotations) */
export function getAnnotationShowLengthsProperty(annotationId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_show_lengths', annotationId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

/** Get reactive property for showing area label (Area annotations) */
export function getAnnotationShowAreaProperty(annotationId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_show_area', annotationId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

/** Get reactive property for showing height labels (Opening annotations) */
export function getAnnotationShowHeightProperty(annotationId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_show_height', annotationId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

/** Get reactive property for showing volume labels (Area annotations) */
export function getAnnotationShowVolumeProperty(annotationId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_show_volume', annotationId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}
